<template>
  <Card variant="primary" size="medium">
    <View direction="column" gap="md">
      <!-- On a phone the panel folds under a title, so it does not push the units below the fold -->
      <View v-if="collapsible" justify="space-between" align="center">
        <Text variant="h5">About this building</Text>
        <IconButton
          icon="chevron-down"
          variant="ghost"
          size="small"
          :class="['toggle', { open }]"
          :aria-expanded="open"
          aria-label="About this building"
          @click="toggle"
        />
      </View>

      <template v-if="!collapsible || open">
        <Map :coordinates="{ latitude: building.location.latitude, longitude: building.location.longitude }" />

        <View direction="column" gap="xs">
          <Text variant="label-medium">{{ building.location.address }}</Text>
          <Text variant="body-medium" color="secondary">{{ cityLine }}</Text>
        </View>

        <View gap="sm" wrap="wrap">
          <Button variant="outline" size="small" class="action" @click="openInMaps">
            <Icon name="link-01" size="small" />
            <span>Open in Maps</span>
          </Button>
          <Button variant="ghost" size="small" class="action" @click="copyAddress">
            <Icon name="copy" size="small" />
            <span>Copy address</span>
          </Button>
        </View>

        <Divider />

        <!-- What every unit shares; set once on the details tab -->
        <View v-if="facts.length || building.accessNotes" direction="column" gap="sm" data-testid="building-facts">
          <View v-for="fact in facts" :key="fact.label" gap="sm" align="start">
            <Text variant="label-small" color="secondary" class="fact-label">{{ fact.label }}</Text>
            <Text variant="body-medium">{{ fact.value }}</Text>
          </View>

          <Hint v-if="building.accessNotes" icon="key">
            <View direction="column" gap="xs">
              <Text variant="label-small" color="secondary">Getting in</Text>
              <Text variant="body-medium" class="access-notes">{{ building.accessNotes }}</Text>
            </View>
          </Hint>
        </View>

        <View v-else direction="column" gap="xs">
          <Text variant="body-small" color="secondary">No shared details yet</Text>
          <Link :to="buildingDetailsRoute(building.id)" color="accent">Add on Building details</Link>
        </View>

        <template v-if="managerName">
          <Divider />

          <View direction="column" gap="sm">
            <View gap="sm" align="center">
              <Avatar :user-name="managerName" show-single-letter size="small" />
              <Text variant="body-medium" color="secondary" class="nowrap">Managed by</Text>
              <Text variant="label-medium">{{ managerName }}</Text>
            </View>

            <View v-if="collaboratorNames.length" gap="sm" align="center">
              <CollaboratorAvatars :names="collaboratorNames" />
              <Text variant="body-medium" color="secondary">
                {{ pluralize('collaborator', collaboratorNames.length, true) }}
              </Text>
            </View>
          </View>
        </template>
      </template>
    </View>
  </Card>
</template>

<script setup lang="ts">
import pluralize from 'pluralize';
import { computed, ref } from 'vue';

import {
  Avatar,
  Button,
  Card,
  Divider,
  Hint,
  Icon,
  IconButton,
  Link,
  Text,
  View,
  useAlertsStore,
} from '@tallo/design-system';
import { CollaboratorAvatars, teamMemberName } from '@tallo/investor/company';
import { buildingDetailsRoute } from '@tallo/investor/navigation';
import {
  BuildingDto,
  buildingAmenityKeys,
  buildingCityLine,
  buildingMapsUrl,
  usePropertyTeamQuery,
} from '@tallo/investor/property';
import { Map, amenityNamesMap } from '@tallo/property';

/** Where the building is and who runs it, beside its units */
const props = defineProps<{
  building: BuildingDto;
  /** Folds the panel under its title, remembered for this browser; off, it always shows */
  collapsible?: boolean;
}>();

const openKey = 'building-panel-open';

const alertsStore = useAlertsStore();
const { data: team } = usePropertyTeamQuery(() => ({ kind: 'building', id: props.building.id }));
const open = ref(readOpen());

const cityLine = computed(() => buildingCityLine(props.building.location));
const fullAddress = computed(() => `${props.building.location.address}, ${cityLine.value}`);
const managerName = computed(() => (props.building.manager ? teamMemberName(props.building.manager) : ''));
const collaboratorNames = computed(() => (team.value?.collaborators ?? []).map(teamMemberName));

// Past this many, the amenities read as a count; the details tab lists them all
const namedAmenities = 3;

/** The shared facts the building has answered, in the order the details tab asks them */
const facts = computed(() => {
  const { yearBuilt, amenities, parking } = props.building;
  const amenityNames = buildingAmenityKeys.filter((key) => amenities?.[key]).map((key) => amenityNamesMap[key]);
  const parkingLine = !parking
    ? ''
    : !parking.hasParking
      ? 'No parking'
      : [
          parking.parkingAvailableSpaces ? `${parking.parkingAvailableSpaces} spaces` : 'Available',
          parking.parkingMonthlyChargePerSpace ? `$${parking.parkingMonthlyChargePerSpace}/mo` : '',
          parking.parkingSpacesAreTandem ? 'tandem' : '',
        ]
          .filter(Boolean)
          .join(' · ');

  return [
    { label: 'Built', value: yearBuilt ? String(yearBuilt) : '' },
    {
      label: 'Amenities',
      value:
        amenityNames.length > namedAmenities
          ? `${amenityNames.slice(0, namedAmenities).join(' · ')} +${amenityNames.length - namedAmenities} more`
          : amenityNames.join(' · '),
    },
    { label: 'Parking', value: parkingLine },
  ].filter((fact) => fact.value);
});

function openInMaps() {
  window.open(buildingMapsUrl(props.building.location), '_blank', 'noopener');
}

async function copyAddress() {
  try {
    await navigator.clipboard.writeText(fullAddress.value);
    alertsStore.success('Address copied');
  } catch {
    alertsStore.error("Couldn't copy the address");
  }
}

function toggle() {
  open.value = !open.value;

  // Browser storage can be missing or blocked (private windows, cleared site data); the panel works without it
  try {
    localStorage.setItem(openKey, String(open.value));
  } catch {
    // Nothing to keep it in: it folds again on the next visit
  }
}

function readOpen(): boolean {
  try {
    return localStorage.getItem(openKey) === 'true';
  } catch {
    return false;
  }
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.nowrap {
  white-space: nowrap;
}

// The labels line up as a column, so the values read as a list
.fact-label {
  flex-shrink: 0;
  width: 4.5rem;
  padding-top: 0.125rem;
}

// Notes are typed with line breaks (buzzer, then parking); they read the way they were written
.access-notes {
  white-space: pre-line;
}

.toggle {
  transition: transform 0.2s var(--t-ease-out);

  &.open {
    transform: rotate(180deg);
  }
}

// On a phone the two actions share the row
.action {
  @include mobile {
    flex: 1;
  }
}
</style>
